import { COPYABLE_MARKS } from '../plugins/Formatting/tools/formatCopying';
import { Editor, Element, Text, type NodeEntry } from 'slate';
import type { CustomText } from '../types/aha-editor';
import { isCodeSnippetElement } from '../types/guards';

type TextStyles =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'textColor';

/**
 * Execute the passed in function without running
 * any normalizations.
 *
 * What's the difference between this and Editor.withoutNormalizing?
 * Editor.withoutNormalizing runs the normalizations after the passed
 * in function has finished executing. This function does not run
 * normalizations at all. USE WITH CARE.
 *
 * In this context, it is necessary because normalizations are messing
 * up with our content. When we have an off-screen editor with an
 * inline and we try to apply a mark to it, the normalizations remove
 * the inline.
 *
 * See CREATE-1791-1
 */
export const suppressNormalizations = (editor: Editor, fn: () => void) => {
  Editor.setNormalizing(editor, false);
  try {
    fn();
  } finally {
    Editor.setNormalizing(editor, true);
  }
};

class MarkUtils {
  static add(editor: Editor, mark: string, value: unknown = true) {
    /*
     * Because marks are applied on leaf nodes, which don't
     * have default normalizations in slate, it is safe to suppress
     * normalizations while toggling them. Addresses CREATE-1791-1.
     */
    suppressNormalizations(editor, () => {
      Editor.addMark(editor, mark, value);
    });
  }

  static remove(editor: Editor, mark: string) {
    /*
     * Because marks are applied on leaf nodes, which don't
     * have default normalizations in slate, it is safe to suppress
     * normalizations while toggling them. Addresses CREATE-1791-1.
     */
    suppressNormalizations(editor, () => {
      Editor.removeMark(editor, mark);
    });
  }

  /**
   * Checks if all text nodes have the same value for a given mark and returns
   * that value, be it true, false, or a string. Undefined is returned if they
   * do not all share the same value.
   */
  private static allNodesHaveSameMark(
    editor: Editor,
    mark: TextStyles
  ): boolean | string | undefined {
    const textNodes = this.getTextNodes(editor);
    let commonValue: string | boolean | undefined;

    for (const [node] of textNodes) {
      if (node.text === '') {
        continue;
      }

      const value = node[mark];

      if (commonValue === undefined) {
        commonValue = value;
      } else if (commonValue !== value) {
        return undefined;
      }
    }

    return commonValue;
  }

  /**
   * Checks if all nodes have the same value for a specific mark or are in code
   * snippets (for the 'code' mark). Returns that value, undefined otherwise.
   */
  static allNodesHaveStyle(
    editor: Editor,
    mark: TextStyles | 'code'
  ): boolean | string | undefined {
    // Code blocks and code snippets are elements, not marks on text nodes
    if (mark === 'code') {
      return this.allNodesAreInCodeSnippet(editor);
    }

    return MarkUtils.allNodesHaveSameMark(editor, mark);
  }

  /**
   * Checks if all text nodes have the same textColor mark.
   * If they do, returns the color string. Otherwise, returns undefined.
   */
  static allNodesHaveSameColor(editor: Editor): undefined | string {
    const color = this.allNodesHaveSameMark(editor, 'textColor');

    if (typeof color === 'boolean') {
      throw new Error(
        'Expected textColor mark to be a string or undefined, but got boolean'
      );
    }

    return color;
  }

  static getTextNodes(editor: Editor): NodeEntry<Text>[] {
    return Array.from(Editor.nodes(editor, { match: Text.isText }));
  }

  static allNodesAreInCodeSnippet(editor: Editor): boolean {
    const textNodes = this.getTextNodes(editor);

    // Check if all text nodes are inside code snippet elements
    for (const [node, path] of textNodes) {
      if (node.text === '') {
        continue;
      }

      const codeParent = Editor.above(editor, {
        at: path,
        match: (n) => Element.isElement(n) && isCodeSnippetElement(n),
      });

      if (!codeParent) {
        return false;
      }
    }
    return textNodes.length > 0;
  }

  // Gets the value of mark at the current editor selection.
  // If the mark is not present, returns undefined.
  static getMarkAtSelection(editor: Editor, mark: string) {
    const marks = Editor.marks(editor);

    if (marks?.[mark]) {
      return marks[mark];
    }
  }

  // Captures the current marks, runs the callback, and restores the previous marks
  static withRestoredMarks(editor: Editor, callback: (editor: Editor) => void) {
    const marks = Editor.marks(editor) || {};

    callback(editor);

    Object.entries(marks).forEach(([key, value]) => {
      Editor.addMark(editor, key, value);
    });
  }

  /**
   * This will copy the "copyable" marks from one text node to another.
   */
  static copyMarks({
    source,
    target,
  }: {
    source: CustomText;
    target: CustomText;
  }) {
    COPYABLE_MARKS.forEach((mark) => {
      const value = source[mark];
      if (value != null) {
        // @ts-expect-error The key/value type are dynamic
        target[mark] = value;
      }
    });
  }
}

export default MarkUtils;
